import * as React from "react";
import apiClient from "@/api-client";
import { Box, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { CaretDownIcon } from "@phosphor-icons/react/dist/ssr/CaretDown";
import { CaretRightIcon } from "@phosphor-icons/react/dist/ssr/CaretRight";
import { CopyIcon } from "@phosphor-icons/react/dist/ssr/Copy";
import { SpeedometerIcon } from "@phosphor-icons/react/dist/ssr/Speedometer";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";

interface CompletionsJsonModalProps {
	open: boolean;
	onClose: () => void;
	conversationId: string | null;
}

const PAGE_SIZE = 20;

interface CompletionLog {
	id: number;
	status?: number;
	createdAt?: string;
	times?: Record<string, string>;
	usage?: {
		completion_tokens?: number;
		prompt_tokens?: number;
		total_tokens?: number;
	};
	tools?: unknown[];
	prompts?: string[];
	context?: Array<{ role?: string; content?: string }>;
	response?: {
		completion?: string;
		content?: string;
		toolCalls?: unknown[];
	};
}

interface CompletionsResponse {
	data: CompletionLog[];
	total: number;
}

export function CompletionsJsonModal({ open, onClose, conversationId }: CompletionsJsonModalProps): React.JSX.Element {
	const [logs, setLogs] = React.useState<CompletionLog[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);
	const [total, setTotal] = React.useState(0);
	const [offset, setOffset] = React.useState(0);
	const [error, setError] = React.useState<string | null>(null);
	const [expandedLogs, setExpandedLogs] = React.useState<string[]>([]);
	const scrollRef = React.useRef<HTMLDivElement | null>(null);

	const isInitialLoad = offset === 0;
	const hasMore = logs.length < total;

	React.useEffect(() => {
		if (!open) return;
		setLogs([]);
		setTotal(0);
		setOffset(0);
		setError(null);
		setExpandedLogs([]);
	}, [open, conversationId]);

	React.useEffect(() => {
		if (!open || !conversationId) return;

		let isCancelled = false;
		const fetchLogs = async () => {
			setIsLoading(true);
			try {
				const response = await apiClient.get<CompletionsResponse>(
					`/conversations/${conversationId}/completions?limit=${PAGE_SIZE}&offset=${offset}`
				);
				if (isCancelled) return;

				const pageData = Array.isArray(response.data?.data) ? response.data.data : [];
				const pageTotal = typeof response.data?.total === "number" ? response.data.total : 0;

				setError(null);
				setTotal(pageTotal);
				setLogs((prev) => (offset === 0 ? pageData : [...prev, ...pageData]));
			} catch (fetchError: any) {
				if (!isCancelled) setError(fetchError?.response?.data?.message || "Failed to fetch completions");
			} finally {
				if (!isCancelled) setIsLoading(false);
			}
		};

		void fetchLogs();
		return () => {
			isCancelled = true;
		};
	}, [conversationId, offset, open]);

	const handleScroll = React.useCallback(() => {
		const element = scrollRef.current;
		if (!element || isLoading || !hasMore) return;

		const threshold = 120;
		const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - threshold;
		if (isNearBottom) setOffset((prev) => prev + PAGE_SIZE);
	}, [hasMore, isLoading]);

	const handleToggleLog = React.useCallback((key: string) => {
		setExpandedLogs((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
	}, []);

	const handleCopy = React.useCallback((text: string) => {
		void navigator.clipboard.writeText(text);
	}, []);

	const formatDate = React.useCallback((dateString?: string) => {
		if (!dateString) return "";
		return new Date(dateString).toLocaleString();
	}, []);

	const renderExpandableJson = React.useCallback(
		(label: string, key: string, value: unknown) => {
			if (!value) return null;
			const isExpanded = expandedLogs.includes(key);

			return (
				<Box sx={{ mb: 1 }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							cursor: "pointer",
							p: 1,
              pl: 2,
							backgroundColor: "var(--mui-palette-background-level1)",
							borderRadius: 1,
							mb: isExpanded ? 1 : 0,
						}}
						onClick={() => handleToggleLog(key)}
					>
						<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
							{label}
						</Typography>
						<IconButton size="small">{isExpanded ? <CaretDownIcon size={16} /> : <CaretRightIcon size={16} />}</IconButton>
					</Box>
					{isExpanded ? (
						<Box sx={{ position: "relative", border: "1px solid var(--mui-palette-divider)", borderRadius: 1, p: 1.5 }}>
							<IconButton size="small" onClick={() => handleCopy(JSON.stringify(value, null, 2))} sx={{ position: "absolute", top: 4, right: 4 }}>
								<CopyIcon size={16} />
							</IconButton>
							<Typography component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
								{JSON.stringify(value, null, 2)}
							</Typography>
						</Box>
					) : null}
				</Box>
			);
		},
		[expandedLogs, handleCopy, handleToggleLog]
	);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					borderBottom: "1px solid var(--mui-palette-divider)",
				}}
			>
				Conversation Completions
				<IconButton onClick={onClose} size="small" aria-label="Close modal">
					<XIcon size={20} />
				</IconButton>
			</DialogTitle>
			<DialogContent ref={scrollRef} onScroll={handleScroll} sx={{ mt: 1, maxHeight: "75vh", overflow: "auto" }}>
				{error ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
						<Typography color="error.main">{error}</Typography>
					</Box>
				) : isLoading && isInitialLoad ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
						<CircularProgress size={28} />
					</Box>
				) : logs.length === 0 ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
						<Typography>No completions found</Typography>
					</Box>
				) : (
					<Box sx={{ mt: 2 }}>
						{logs.map((log) => {
							const logKey = String(log.id);
							const isExpanded = expandedLogs.includes(logKey);

							return (
								<Box
									key={log.id}
									sx={{
										border: "1px solid var(--mui-palette-divider)",
										borderRadius: 1,
										mb: 2,
										overflow: "hidden",
									}}
								>
									<Box
										sx={{
											p: 2,
											backgroundColor: "var(--mui-palette-background-level1)",
											borderBottom: isExpanded ? "1px solid var(--mui-palette-divider)" : "none",
											cursor: "pointer",
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
										}}
										onClick={() => handleToggleLog(logKey)}
									>
										<Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxWidth: "calc(100% - 180px)" }}>
											<Typography
												variant="subtitle2"
												sx={{ fontWeight: 600, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
											>
												[{log.id}] {log.response?.completion || log.response?.content || "No completion"}
											</Typography>
											<Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
												<Chip label={`Status: ${log.status ?? "-"}`} size="small" variant="outlined" color="default" />
												<Chip
													label={`Total: ${log.usage?.total_tokens || 0}`}
													size="small"
													variant="outlined"
													color="default"
													icon={<SpeedometerIcon size={14} />}
												/>
											</Stack>
										</Box>
										<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
											<Typography variant="caption" color="text.secondary" style={{ whiteSpace: "nowrap" }}>
												{formatDate(log.createdAt)}
											</Typography>
											<IconButton size="small">{isExpanded ? <CaretDownIcon size={16} /> : <CaretRightIcon size={16} />}</IconButton>
										</Stack>
									</Box>

									{isExpanded ? (
										<Box sx={{ p: 2 }}>
											<Box sx={{ mb: 2, p: 1.5, backgroundColor: "var(--mui-palette-background-level1)", borderRadius: 1 }}>
												<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
													Usage Statistics
												</Typography>
												<Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
													<Chip label={`Total: ${log.usage?.total_tokens || 0}`} size="small" variant="outlined" color="default" />
													<Chip label={`Prompt: ${log.usage?.prompt_tokens || 0}`} size="small" variant="outlined" color="default" />
													<Chip label={`Completion: ${log.usage?.completion_tokens || 0}`} size="small" variant="outlined" color="default" />
												</Stack>
											</Box>

											{renderExpandableJson("Times", `times-${logKey}`, log.times)}
											{renderExpandableJson("Context", `context-${logKey}`, log.context)}
											{renderExpandableJson("Prompts", `prompts-${logKey}`, log.prompts)}
											{renderExpandableJson("Response", `response-${logKey}`, log.response)}
											{renderExpandableJson("Tools", `tools-${logKey}`, log.tools)}
										</Box>
									) : null}
								</Box>
							);
						})}
					</Box>
				)}

				{isLoading && !isInitialLoad ? (
					<Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
						<CircularProgress size={20} />
					</Box>
				) : null}
			</DialogContent>
		</Dialog>
	);
}
